<template>
  <div class="max-w-7xl mx-auto">
    <!-- Page Header -->
    <div class="text-center mb-8">
      <h1 class="text-3xl font-bold mb-4">📊 My Portfolio</h1>
      <p class="text-base-content/70 max-w-2xl mx-auto">
        Track your competition entries, performance, and trading history across all competitions.
      </p>
    </div>

    <!-- Portfolio Overview Stats -->
    <div class="grid md:grid-cols-4 gap-4 mb-8">
      <div class="card bg-base-100 card-bordered">
        <div class="card-body text-center">
          <div class="text-2xl font-bold text-primary">{{ portfolioStats.totalEntries }}</div>
          <div class="text-sm text-base-content/70">Total Entries</div>
        </div>
      </div>
      <div class="card bg-base-100 card-bordered">
        <div class="card-body text-center">
          <div class="text-2xl font-bold text-success">{{ portfolioStats.activeEntries }}</div>
          <div class="text-sm text-base-content/70">Active Entries</div>
        </div>
      </div>
      <div class="card bg-base-100 card-bordered">
        <div class="card-body text-center">
          <div class="text-2xl font-bold" :class="portfolioStats.avgReturn >= 0 ? 'text-success' : 'text-error'">
            {{ portfolioStats.avgReturn >= 0 ? '+' : '' }}{{ portfolioStats.avgReturn.toFixed(1) }}%
          </div>
          <div class="text-sm text-base-content/70">Avg Return</div>
        </div>
      </div>
      <div class="card bg-base-100 card-bordered">
        <div class="card-body text-center">
          <div class="text-2xl font-bold text-warning">{{ portfolioStats.wins }}</div>
          <div class="text-sm text-base-content/70">Top 3 Finishes</div>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="flex justify-center items-center py-12">
      <span class="loading loading-spinner loading-lg"></span>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-error mb-8">
      <i class="mdi mdi-alert-circle h-6 w-6"></i>
      <span>{{ error }}</span>
    </div>

    <!-- Portfolio Content -->
    <div v-else class="space-y-8">
      <!-- Upcoming Entries -->
      <div v-if="upcomingEntries.length > 0">
        <h2 class="text-2xl font-bold mb-4 flex items-center gap-2">
          <i class="mdi mdi-clock-outline text-primary"></i>
          Upcoming Entries
        </h2>
        <div class="space-y-4">
          <div 
            v-for="entry in upcomingEntries" 
            :key="entry.entry_id" 
            class="card bg-base-100 card-bordered"
          >
            <div class="card-body">
              <div class="flex justify-between items-start">
                <div class="flex-1">
                  <h3 class="text-lg font-semibold mb-2">{{ entry.competition_name }}</h3>
                  <div class="flex items-center gap-4 text-sm mb-3">
                    <span class="text-base-content/70">Starts: {{ new Date(entry.competition_start_date).toLocaleDateString() }}</span>
                    <span class="text-base-content/70">Duration: {{ entry.competition_duration_days }} days</span>
                    <span class="badge badge-outline">{{ entry.display_name }}</span>
                  </div>
                  
                  <!-- Stock Picks Preview -->
                  <div v-if="entry.stock_picks && entry.stock_picks.length > 0">
                    <h4 class="font-medium text-sm mb-2">Your Portfolio</h4>
                    <div class="flex flex-wrap gap-2">
                      <span 
                        v-for="stock in entry.stock_picks" 
                        :key="stock.symbol"
                        class="badge badge-primary badge-sm"
                      >
                        {{ stock.symbol }}
                        <span v-if="entry.stock_picks.length > 1" class="ml-1">
                          {{ ((stock.weight || 0) * 100).toFixed(0) }}%
                        </span>
                      </span>
                    </div>
                  </div>
                </div>
                
                <div class="flex gap-2">
                  <router-link 
                    :to="`/competition/${entry.competition_id}`" 
                    class="btn btn-ghost btn-sm"
                  >
                    <i class="mdi mdi-eye"></i>
                    View Competition
                  </router-link>
                  <button 
                    @click="editEntry(entry)"
                    class="btn btn-outline btn-sm"
                  >
                    <i class="mdi mdi-pencil"></i>
                    Edit Entry
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Active Entries -->
      <div v-if="activeEntries.length > 0">
        <h2 class="text-2xl font-bold mb-4 flex items-center gap-2">
          <i class="mdi mdi-chart-line text-success"></i>
          Active Entries
        </h2>
        <div class="space-y-4">
          <div 
            v-for="entry in activeEntries" 
            :key="entry.entry_id" 
            class="card bg-base-100 card-bordered"
          >
            <div class="card-body">
              <div class="flex justify-between items-start">
                <div class="flex-1">
                  <h3 class="text-lg font-semibold mb-2">{{ entry.competition_name }}</h3>
                  <div class="flex items-center gap-4 text-sm mb-3">
                    <span class="text-base-content/70">Ends: {{ new Date(entry.competition_end_date).toLocaleDateString() }}</span>
                    <span class="text-base-content/70">Rank: {{ entry.rank || 'N/A' }}</span>
                    <span class="badge badge-outline">{{ entry.display_name }}</span>
                  </div>
                  
                  <!-- Performance -->
                  <div class="grid md:grid-cols-3 gap-4 mb-3">
                    <div>
                      <span class="text-sm text-base-content/70">Current Return</span>
                      <div class="font-semibold" :class="entry.total_return >= 0 ? 'text-success' : 'text-error'">
                        {{ entry.total_return >= 0 ? '+' : '' }}{{ entry.total_return.toFixed(2) }}%
                      </div>
                    </div>
                    <div>
                      <span class="text-sm text-base-content/70">Portfolio Value</span>
                      <div class="font-semibold">${{ entry.portfolio_value.toFixed(2) }}</div>
                    </div>
                    <div>
                      <span class="text-sm text-base-content/70">Daily Avg</span>
                      <div class="font-semibold" :class="entry.average_daily_return >= 0 ? 'text-success' : 'text-error'">
                        {{ entry.average_daily_return >= 0 ? '+' : '' }}{{ entry.average_daily_return.toFixed(2) }}%
                      </div>
                    </div>
                  </div>
                  
                  <!-- Stock Picks Performance -->
                  <div v-if="entry.stock_picks && entry.stock_picks.length > 0">
                    <h4 class="font-medium text-sm mb-2">Portfolio Performance</h4>
                    <div class="space-y-2">
                      <div 
                        v-for="stock in entry.stock_picks" 
                        :key="stock.symbol"
                        class="flex justify-between items-center p-2 bg-base-200 rounded-lg"
                      >
                        <div class="flex items-center gap-2">
                          <span class="font-semibold">{{ stock.symbol }}</span>
                          <span v-if="entry.stock_picks.length > 1" class="text-xs text-base-content/70">
                            {{ ((stock.weight || 0) * 100).toFixed(0) }}%
                          </span>
                        </div>
                        <div class="text-right">
                          <div class="font-semibold" :class="stock.return_percentage >= 0 ? 'text-success' : 'text-error'">
                            {{ stock.return_percentage >= 0 ? '+' : '' }}{{ stock.return_percentage.toFixed(2) }}%
                          </div>
                          <div class="text-xs text-base-content/70">
                            ${{ stock.current_price.toFixed(2) }}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div class="flex gap-2">
                  <router-link 
                    :to="`/active-competition/${entry.competition_id}`" 
                    class="btn btn-primary btn-sm"
                  >
                    <i class="mdi mdi-chart-line"></i>
                    Live View
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Historical Entries -->
      <div v-if="historicalEntries.length > 0">
        <h2 class="text-2xl font-bold mb-4 flex items-center gap-2">
          <i class="mdi mdi-history text-base-content/70"></i>
          Competition History
        </h2>
        <div class="space-y-4">
          <div 
            v-for="entry in historicalEntries" 
            :key="entry.entry_id" 
            class="card bg-base-100 card-bordered"
          >
            <div class="card-body">
              <div class="flex justify-between items-start">
                <div class="flex-1">
                  <h3 class="text-lg font-semibold mb-2">{{ entry.competition_name }}</h3>
                  <div class="flex items-center gap-4 text-sm mb-3">
                    <span class="text-base-content/70">Ended: {{ new Date(entry.competition_end_date).toLocaleDateString() }}</span>
                    <span class="text-base-content/70">Final Rank: {{ entry.rank || 'N/A' }}</span>
                    <span class="badge badge-outline">{{ entry.display_name }}</span>
                    <span v-if="entry.rank <= 3 && entry.competition_is_ended" class="badge badge-warning">
                      {{ entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : '🥉' }} Winner
                    </span>
                  </div>
                  
                  <!-- Final Performance -->
                  <div class="grid md:grid-cols-3 gap-4 mb-3">
                    <div>
                      <span class="text-sm text-base-content/70">Final Return</span>
                      <div class="font-semibold" :class="entry.total_return >= 0 ? 'text-success' : 'text-error'">
                        {{ entry.total_return >= 0 ? '+' : '' }}{{ entry.total_return.toFixed(2) }}%
                      </div>
                    </div>
                    <div>
                      <span class="text-sm text-base-content/70">Final Value</span>
                      <div class="font-semibold">${{ entry.portfolio_value.toFixed(2) }}</div>
                    </div>
                    <div>
                      <span class="text-sm text-base-content/70">Performance</span>
                      <div class="font-semibold" :class="entry.total_return >= 0 ? 'text-success' : 'text-error'">
                        {{ entry.total_return >= 0 ? 'Profitable' : 'Loss' }}
                      </div>
                    </div>
                  </div>
                </div>
                
                <div class="flex gap-2">
                  <router-link 
                    :to="`/competition/${entry.competition_id}`" 
                    class="btn btn-ghost btn-sm"
                  >
                    <i class="mdi mdi-eye"></i>
                    View Results
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Empty State -->
      <div v-if="upcomingEntries.length === 0 && activeEntries.length === 0 && historicalEntries.length === 0" class="text-center py-12">
        <div class="max-w-md mx-auto">
          <div class="text-6xl mb-4">📊</div>
          <h4 class="text-lg font-semibold mb-2">No entries yet</h4>
          <p class="text-base-content/70 mb-6">
            Start participating in competitions to build your portfolio and track your performance.
          </p>
          <router-link to="/compete" class="btn btn-primary">
            Browse Competitions
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useRoute } from 'vue-router'
import { useCompetitionStore } from '../stores/competition'
import { useAuthStore } from '../stores/auth'

const route = useRoute()
const competitionStore = useCompetitionStore()
const authStore = useAuthStore()

const loading = ref(false)
const error = ref('')
const allEntries = ref([])

// Computed properties for different entry categories
const upcomingEntries = computed(() => {
  return allEntries.value.filter(entry => !entry.competition_is_locked)
})

const activeEntries = computed(() => {
  return allEntries.value.filter(entry => entry.competition_is_locked && !entry.competition_is_ended)
})

const historicalEntries = computed(() => {
  return allEntries.value.filter(entry => entry.competition_is_ended)
})

const portfolioStats = computed(() => {
  const totalEntries = allEntries.value.length
  const activeEntries = allEntries.value.filter(entry => entry.competition_is_locked && !entry.competition_is_ended).length
  const avgReturn = allEntries.value.length > 0 
    ? allEntries.value.reduce((sum, entry) => sum + (entry.total_return || 0), 0) / allEntries.value.length
    : 0
  const wins = allEntries.value.filter(entry => entry.rank <= 3 && entry.competition_is_ended).length

  return {
    totalEntries,
    activeEntries,
    avgReturn,
    wins
  }
})

const fetchMyEntries = async () => {
  // Ensure auth is initialized first
  if (!authStore.user && authStore.token) {
    await authStore.initializeAuth()
  }
  
  if (!authStore.isAuthenticated) return
  
  loading.value = true
  error.value = ''
  
  try {
    const entries = await competitionStore.fetchMyEntries()
    allEntries.value = entries
  } catch (err) {
    error.value = err.message || 'Failed to load your entries'
  } finally {
    loading.value = false
  }
}

const editEntry = (entry) => {
  // Navigate to edit entry form
  // This would need to be implemented
  console.log('Edit entry:', entry)
}

// Watch for route changes to refetch data
watch(() => route.path, () => {
  if (route.path === '/my-portfolio') {
    fetchMyEntries()
  }
}, { immediate: true })

onMounted(async () => {
  // Ensure auth is initialized on mount
  if (authStore.token && !authStore.user) {
    await authStore.initializeAuth()
  }
  await fetchMyEntries()
})
</script> 